import { createContext, useContext, useEffect, useState, ReactNode } from "react";
import { getUserStorage, setUserStorage, removeUserStorage, getAccessToken, setAccessToken as setAccessTokenStorage, removeAccessToken } from "../services/authService";
import { useNavigate } from "react-router-dom";
import { setAccessTokenRef, setAuthHelper } from "../services/authHelper";

export interface UserNotifications {
  proRelease?: boolean;
}

export interface AuthUser {
  userImage?: { fileName: string; updatedAt?: string } | null;
  username?: string;
  _id?: string;
  email?: string;
  plan?: 'basic' | 'pro';
  isDeleted?: boolean;
  deletedAt?: string | null;
  notifications?: UserNotifications;
}

export interface AuthContextValue {
  user: AuthUser;
  login: (userData: AuthUser, token: string) => void;
  logout: (redirect?: boolean) => void;
  accessToken: string | null;
  setAccessToken: (token: string | null) => void;
  updateUsername: (newValue: string) => void;
  updateUserImage: (fileName: string, updatedAt?: string) => void;
  deleteUserImage: () => void;
  restoreAccount: (userData: AuthUser, token: string) => void;
  setNotifications: (notifications: UserNotifications) => void;
}

const AuthContext = createContext<AuthContextValue | null>(null);

export const AuthProvider = ({ children }: { children: ReactNode }) => {
  const [user, setUser] = useState<AuthUser | null>(() => {
    const stored = getUserStorage();
    return stored ? JSON.parse(stored) : null;
  });
  const navigate = useNavigate();
  const [accessToken, setAccessToken] = useState<string | null>(null);

  const login = (userData: AuthUser, token: string) => {
    setUser(userData);
    setUserStorage(userData);

    setAccessTokenStorage(token)
    setAccessToken(token);
    setAccessTokenRef(token);

    navigate("/dashboard", { replace: true });
  };

  const logout = (redirect = true) => {
    setAccessToken(null);
    setUser(null);
    removeAccessToken();
    removeUserStorage();
    if (redirect) navigate("/login");
  };

  const updateUsername = (newValue: string) => {
    setUser(prev => {
      const updated = { ...prev, username: newValue };
      setUserStorage(updated);
      return updated;
    });
  }

  const updateUserImage = (fileName: string, updatedAt?: string) => {
    setUser(prev => {
      const updated = { ...prev, userImage: { fileName, updatedAt }};
      setUserStorage(updated);
      return updated;
    });
  }

  const deleteUserImage = () => {
    setUser(prev => {
      const updated = { ...prev, userImage: null };
      setUserStorage(updated);
      return updated;
    });
  }

  const restoreAccount = (userData: AuthUser, token: string) => {
    setUser(userData);
    setUserStorage(userData);
    setAccessTokenStorage(token);
    setAccessToken(token);
    setAccessTokenRef(token);
  };

  const setNotifications = (notifications: UserNotifications) => {
    setUser(prev => {
      const updated = { ...prev, notifications };
      setUserStorage(updated);
      return updated;
    });
  };

  useEffect(() => {
    const savedToken = getAccessToken();
    if (savedToken) {
      setAccessToken(savedToken);
      setAccessTokenRef(savedToken);
    }
    setAuthHelper(logout);
  }, []);

  return (
    <AuthContext.Provider value={{
      user: user as AuthUser,
      login,
      logout,
      accessToken,
      setAccessToken,
      updateUsername,
      updateUserImage,
      deleteUserImage,
      restoreAccount,
      setNotifications,
    }}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) throw new Error('useAuth must be used within an AuthProvider');
  return context;
};